import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSession } from "@/auth/session";
import ConvenioModal from "../components/ConvenioModal";
import RepactacionModal from "../components/RepactacionModal";
import CompromisoModal from "../components/CompromisoModal";
import Badge from "@/components/base/Badge";
import { buildReceiptHtml, downloadReceiptHtml, formatDocumentLabel, openPrintWindow } from "@/lib/receipt";
import {
  changeContractMeter,
  changeContractStatus,
  createContract,
  deleteContractBillingProfile,
  createMeter,
  getContractById,
  getNextContractNumber,
  getCustomerById,
  listBillingDocuments,
  listContracts,
  listInstallmentsByContract,
  listMeterReadings,
  listOperations,
  listPaymentsByContract,
  getPaymentDebtSummary,
  listSectors,
  listTariffs,
  upsertContractBillingProfile,
  updateCustomer,
  updateCustomerRut,
  type ChangeContractMeterPayload,
  type ContractDetail,
  type ContractListItem,
  type CustomerDetail,
  type BillingDocumentItem,
  type InstallmentItem,
  type MeterReadingItem,
  type OperationItem,
  type PaymentItem,
  type AdminSession,
  type SectorItem,
  type TariffOption,
  updateContract,
  updateMeter,
} from "@/lib/api";
import EditarClienteModal from "@/pages/clientes/components/EditarClienteModal";
import ActualizarRutModal from "@/pages/clientes/components/ActualizarRutModal";
import CambioMedidorModal from "@/pages/clientes/components/CambioMedidorModal";
import NuevoContratoModal, { type NewContractPayload } from "@/pages/clientes/components/NuevoContratoModal";
import QuitarVigenciaContratoModal from "@/pages/clientes/components/QuitarVigenciaContratoModal";

const estadoVariant: Record<string, "success" | "error" | "warning" | "neutral"> = {
  activo: "success",
  moroso: "warning",
  cortado: "error",
  inactivo: "neutral",
};

const estadoLabel: Record<string, string> = {
  activo: "Activo",
  moroso: "Moroso",
  cortado: "Cortado",
  inactivo: "Inactivo",
};

const contractEstadoVariant: Record<string, "success" | "error" | "warning"> = {
  vigente: "success",
  suspendido: "warning",
  cortado: "error",
};

type UiContract = {
  id: string;
  customerId: string;
  meterId: string;
  contractNumber: string;
  direccion: string;
  sector: string;
  estado: "vigente" | "suspendido" | "cortado";
  tarifa: string;
  medidor: string;
  tipoMedidor: string;
  selloMedidor: string;
  billingRecipientName?: string;
  fechaAlta: string;
  billingDocumentType: "39" | "41" | "33" | "34";
  meterBrand?: string;
  meterManufacturingYear?: number | null;
};

const emptyCustomer: CustomerDetail = {
  id: "",
  customerType: "domiciliario",
  customerCategory: "usuario",
  nombre: "",
  rut: "",
  direccion: "",
  estado: "activo",
  telefono: "",
  email: "",
  rutChanges: [],
};

export default function ClienteDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session, hasModule } = useSession();

  const [activeTab, setActiveTab] = useState<"consumo" | "medidor" | "pagos" | "documentos" | "convenios" | "repactaciones" | "compromisos" | "atenciones">("consumo");
  const [showConvenioModal, setShowConvenioModal] = useState(false);
  const [showRepactacionModal, setShowRepactacionModal] = useState(false);
  const [showCompromisoModal, setShowCompromisoModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [showActualizarRutModal, setShowActualizarRutModal] = useState(false);
  const [showNuevoContratoModal, setShowNuevoContratoModal] = useState(false);
  const [showEditContratoModal, setShowEditContratoModal] = useState(false);
  const [showOwnershipActionModal, setShowOwnershipActionModal] = useState(false);
  const [showTransferContractModal, setShowTransferContractModal] = useState(false);
  const [showCambioMedidorModal, setShowCambioMedidorModal] = useState(false);
  const [showOwnershipHistoryModal, setShowOwnershipHistoryModal] = useState(false);
  const [showRutHistoryModal, setShowRutHistoryModal] = useState(false);

  const [customer, setCustomer] = useState<CustomerDetail>(emptyCustomer);
  const [contracts, setContracts] = useState<UiContract[]>([]);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [selectedContractDetail, setSelectedContractDetail] = useState<ContractDetail | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [readings, setReadings] = useState<MeterReadingItem[]>([]);
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [documents, setDocuments] = useState<BillingDocumentItem[]>([]);
  const [installments, setInstallments] = useState<InstallmentItem[]>([]);
  const [operations, setOperations] = useState<OperationItem[]>([]);
  const [deudaTotal, setDeudaTotal] = useState(0);
  const [tariffOptions, setTariffOptions] = useState<TariffOption[]>([]);
  const [sectorOptions, setSectorOptions] = useState<SectorItem[]>([]);

  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingTab, setLoadingTab] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [isSavingContract, setIsSavingContract] = useState(false);
  const [isSavingRut, setIsSavingRut] = useState(false);
  const [isSavingMeterChange, setIsSavingMeterChange] = useState(false);

  const selectedContract = useMemo(
    () => contracts.find((c) => c.id === selectedContractId) ?? null,
    [contracts, selectedContractId],
  );
  const canUpdateRut = hasModule("configuracion");

  useEffect(() => {
    let cancelled = false;

    async function loadBase(): Promise<void> {
      if (!session || !id) return;
      setError(null);
      setLoadingCustomer(true);
      setLoadingContracts(true);
      try {
        const [customerDetail, allContracts, tariffs, sectors] = await Promise.all([
          getCustomerById(session, id),
          listContracts(session),
          listTariffs(session),
          listSectors(session),
        ]);

        if (cancelled) return;

        const customerContracts = allContracts
          .filter((c) => c.customerId === id)
          .map(mapApiContractToUi);

        setCustomer(customerDetail);
        setContracts(customerContracts);
        setTariffOptions(tariffs);
        setSectorOptions(sectors);
        setSelectedContractId(customerContracts[0]?.id ?? null);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "No se pudo cargar ficha de cliente");
        }
      } finally {
        if (!cancelled) {
          setLoadingCustomer(false);
          setLoadingContracts(false);
        }
      }
    }

    loadBase();
    return () => {
      cancelled = true;
    };
  }, [id, session]);

  useEffect(() => {
    let cancelled = false;

    async function loadContractDetail(): Promise<void> {
      if (!session || !selectedContractId) {
        setSelectedContractDetail(null);
        return;
      }

      try {
        const detail = await getContractById(session, selectedContractId);
        if (!cancelled) {
          setSelectedContractDetail(detail);
        }
      } catch (e) {
        if (!cancelled) {
          setSelectedContractDetail(null);
          setError(e instanceof Error ? e.message : "No se pudo cargar detalle del contrato");
        }
      }
    }

    void loadContractDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedContractId, session]);

  useEffect(() => {
    let cancelled = false;

    async function loadTabData(): Promise<void> {
      if (!session || !selectedContract) return;
      setLoadingTab(true);
      setError(null);
      try {
        const [allReadings, contractPayments, contractDocuments, contractInstallments, allOperations, debtSummary] =
          await Promise.all([
            listMeterReadings(session),
            listPaymentsByContract(session, selectedContract.id),
            listBillingDocuments(session, undefined, selectedContract.id),
            listInstallmentsByContract(session, selectedContract.id),
            listOperations(session),
            // La deuda la calcula el backend: es el unico que ve notas de credito,
            // arrastres de saldo anterior y repactaciones. Sumar documentos aca da mal.
            getPaymentDebtSummary(session, selectedContract.id).catch(() => null),
          ]);

        if (cancelled) return;

        setReadings(allReadings.filter((r) => r.contractId === selectedContract.id));
        setPayments(contractPayments);
        setDocuments(contractDocuments);
        setInstallments(contractInstallments);
        setOperations(allOperations.filter((o) => o.contractId === selectedContract.id));
        setDeudaTotal(debtSummary?.debtSummary.outstandingAmount ?? 0);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "No se pudieron cargar tabs del contrato");
        }
      } finally {
        if (!cancelled) setLoadingTab(false);
      }
    }

    loadTabData();
    return () => {
      cancelled = true;
    };
  }, [selectedContract, session]);

  const consumo = useMemo(() => {
    return readings
      .slice(0, 12)
      .reverse()
      .map((r) => ({
        periodo: r.readingPeriodId,
        mes: new Date(r.readAt).toLocaleDateString("es-CL", { month: "short", year: "2-digit" }),
        val: Number(r.consumptionM3 || 0),
        anterior: r.previousReading != null ? Number(r.previousReading) : null,
        actual: r.currentReading != null ? Number(r.currentReading) : null,
        fecha: r.readAt,
        estado: r.status ?? "",
      }));
  }, [readings]);

  const maxConsumo = Math.max(...consumo.map((i) => i.val), 1);
  const promedioConsumo = consumo.length > 0 ? Math.round(consumo.reduce((acc, i) => acc + i.val, 0) / consumo.length) : 0;
  const totalConsumo = consumo.reduce((acc, i) => acc + i.val, 0);

  const documentosContrato = useMemo(() => {
    if (!selectedContract) return [];
    return documents.filter((doc) => doc.contractId === selectedContract.id);
  }, [documents, selectedContract]);

  // Sugerencia de monto para el modal de repactación: boletas vigentes sin nota de
  // crédito. Es una referencia para el operador, no el saldo real del contrato.
  const deudaBoletas = useMemo(
    () =>
      documentosContrato
        .filter((doc) => doc.documentType !== "61")
        .filter((doc) => !["paid", "draft"].includes(doc.status) && doc.cancellationStatus !== "approved")
        .reduce((acc, doc) => acc + doc.totalAmount, 0),
    [documentosContrato],
  );

  const latestReading = useMemo(() => {
    return (
      readings
        .filter((reading) => reading.contractId === selectedContract?.id)
        .slice()
        .sort((a, b) => new Date(b.readAt).getTime() - new Date(a.readAt).getTime())[0] ?? null
    );
  }, [readings, selectedContract?.id]);

  function openEditContrato(contract: UiContract): void {
    setSelectedContractId(contract.id);
    setShowEditContratoModal(true);
  }

  async function onEditContract(payload: NewContractPayload): Promise<void> {
    if (!session || !selectedContract) return;
    setIsSavingContract(true);
    setError(null);

    try {
      const [tariffs, sectors] = await Promise.all([
        tariffOptions.length > 0 ? Promise.resolve(tariffOptions) : listTariffs(session),
        sectorOptions.length > 0 ? Promise.resolve(sectorOptions) : listSectors(session),
      ]);
      const matchedTariff = tariffs.find((t) => t.id === payload.tariffId) ?? tariffs[0];
      if (!matchedTariff) throw new Error("No hay tarifas disponibles para actualizar contrato");
      const matchedSector = sectors.find((s) => s.id === payload.sectorId) ?? sectors[0];
      if (!matchedSector) throw new Error("No hay sectores disponibles para actualizar contrato");

      await updateContract(session, selectedContract.id, {
        meterId: selectedContract.meterId,
        tariffId: matchedTariff.id,
        contractDate: payload.startDate,
        startAddress: payload.address,
        sector: matchedSector.name,
        billingDocumentType: payload.billingDocumentType,
      });

      await saveContractBillingRecipient(session, selectedContract.id, customer.rut, payload.billingRecipientName);

      if (selectedContract.meterId) {
        await updateMeter(session, selectedContract.meterId, {
          serialNumber: payload.meterNumber || undefined,
          sealNumber: payload.sealNumber || undefined,
          meterType: payload.meterType || undefined,
        });
      }

      await changeContractStatus(session, selectedContract.id, {
        toStatus: payload.status === "vigente" ? "active" : "inactive",
        reason: "Actualizacion manual desde ficha cliente",
      });

      setContracts((prev) =>
        prev.map((c) =>
          c.id === selectedContract.id
            ? {
                ...c,
                direccion: payload.address,
                tarifa: matchedTariff.name,
                sector: matchedSector.name,
                estado: payload.status,
                medidor: payload.meterNumber,
                tipoMedidor: payload.meterType,
                selloMedidor: payload.sealNumber,
                fechaAlta: payload.startDate,
                billingDocumentType: payload.billingDocumentType,
                billingRecipientName: payload.billingRecipientName,
              }
            : c,
        ),
      );
      setShowEditContratoModal(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo actualizar contrato");
    } finally {
      setIsSavingContract(false);
    }
  }

  async function onCreateContract(payload: NewContractPayload): Promise<void> {
    if (!session || !id) return;
    setIsSavingContract(true);
    setError(null);

    try {
      const [tariffs, sectors] = await Promise.all([
        tariffOptions.length > 0 ? Promise.resolve(tariffOptions) : listTariffs(session),
        sectorOptions.length > 0 ? Promise.resolve(sectorOptions) : listSectors(session),
      ]);
      const matchedTariff = tariffs.find((t) => t.id === payload.tariffId) ?? tariffs[0];
      if (!matchedTariff) throw new Error("No hay tarifas disponibles para crear contrato");
      const matchedSector = sectors.find((s) => s.id === payload.sectorId) ?? sectors[0];
      if (!matchedSector) throw new Error("No hay sectores disponibles para crear contrato");

      const meter = await createMeter(session, {
        meterType: payload.meterType,
        serialNumber: payload.meterNumber,
        sealNumber: payload.sealNumber,
        status: payload.status === "cortado" ? "inactive" : "active",
      });

      const { nextNumber } = await getNextContractNumber(session);
      const contractNumber = String(nextNumber);
      const created = await createContract(session, {
        customerId: id,
        meterId: meter.id,
        tariffId: matchedTariff.id,
        contractNumber,
        contractDate: payload.startDate,
        startAddress: payload.address,
        sector: matchedSector.name,
        status: payload.status === "vigente" ? "active" : "inactive",
        billingDocumentType: payload.billingDocumentType,
      });

      await saveContractBillingRecipient(session, created.id, customer.rut, payload.billingRecipientName);

      const complete = {
        ...created,
        tariffName: matchedTariff.name,
        meterSerialNumber: payload.meterNumber,
        meterType: payload.meterType,
        meterSealNumber: payload.sealNumber,
        billingRecipientName: payload.billingRecipientName,
      } as ContractListItem;

      const mapped = mapApiContractToUi(complete);
      setContracts((prev) => [mapped, ...prev]);
      setSelectedContractId(mapped.id);
      setSuccessMessage(`Contrato creado con N° ${contractNumber}`);
      setTimeout(() => setSuccessMessage(null), 6000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo crear contrato");
    } finally {
      setIsSavingContract(false);
    }
  }

  const tabs = [
    { id: "consumo", label: "Consumo", icon: "ri-drop-line" },
    { id: "medidor", label: "Medidor", icon: "ri-dashboard-3-line" },
    { id: "pagos", label: "Pagos", icon: "ri-money-dollar-circle-line" },
    { id: "documentos", label: "Documentos", icon: "ri-receipt-line" },
    { id: "convenios", label: "Convenios", icon: "ri-file-list-3-line" },
    { id: "repactaciones", label: "Repactaciones", icon: "ri-refresh-line" },
    { id: "compromisos", label: "Compromisos", icon: "ri-shake-hands-line" },
    { id: "atenciones", label: "Atenciones", icon: "ri-customer-service-2-line" },
  ] as const;

  async function onEditCustomer(updated: {
    nombre: string;
    rut: string;
    categoria?: "socio" | "usuario";
    genero?: "" | "masculino" | "femenino";
    telefono: string;
    email: string;
    direccion: string;
    estado: string;
  }): Promise<void> {
    if (!session || !id) return;

    const [rutBody, rutDvRaw] = updated.rut.split("-");
    const rut = (rutBody ?? "").replace(/[^0-9]/g, "");
    const dv = (rutDvRaw ?? customer.dv ?? "").replace(/[^0-9kK]/g, "").toUpperCase();

    const nameParts = updated.nombre.trim().split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] ?? "Cliente";
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : (customer.lastName ?? "APR");
    const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : undefined;

    await updateCustomer(session, id, {
      customerType: customer.customerType,
      customerCategory: updated.categoria ?? customer.customerCategory ?? "usuario",
      gender: updated.genero || undefined,
      rut,
      dv,
      firstName,
      lastName,
      middleName,
      birthDate: customer.birthDate,
      deathDate: customer.deathDate,
      address: updated.direccion,
      phone: updated.telefono || "-",
      email: updated.email || "sin-email@apr.local",
      joinDate: customer.joinDate ?? new Date().toISOString().slice(0, 10),
      memberNumber: customer.memberNumber,
    });

    const refreshed = await getCustomerById(session, id);

    setCustomer(refreshed);
  }

  async function onUpdateRut(nextRut: string): Promise<void> {
    if (!session || !id) return;

    const [rutBody, rutDvRaw] = nextRut.split("-");
    const rut = (rutBody ?? "").replace(/[^0-9]/g, "");
    const dv = (rutDvRaw ?? "").replace(/[^0-9kK]/g, "").toUpperCase();

    if (!rut || !dv) {
      setError("El nuevo RUT no es válido.");
      return;
    }

    setIsSavingRut(true);
    setError(null);

    try {
      await updateCustomerRut(session, id, `${rut}-${dv}`);

      const refreshed = await getCustomerById(session, id);
      setCustomer(refreshed);
      setShowActualizarRutModal(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo actualizar RUT");
    } finally {
      setIsSavingRut(false);
    }
  }

  async function refreshCustomerContracts(nextSelectedContractId?: string | null): Promise<void> {
    if (!session || !id) return;
    const [customerDetail, allContracts] = await Promise.all([
      getCustomerById(session, id),
      listContracts(session),
    ]);

    const customerContracts = allContracts
      .filter((c) => c.customerId === id)
      .map(mapApiContractToUi);

    setCustomer(customerDetail);
    setContracts(customerContracts);
    const fallbackId = nextSelectedContractId ?? selectedContractId;
    setSelectedContractId(customerContracts.find((row) => row.id === fallbackId)?.id ?? customerContracts[0]?.id ?? null);
  }

  async function onOwnershipActionCompleted(message: string): Promise<void> {
    setShowOwnershipActionModal(false);
    setShowTransferContractModal(false);
    await refreshCustomerContracts(selectedContractId);
    setSuccessMessage(message);
  }

  async function refreshSelectedContract(contractId: string): Promise<void> {
    if (!session) return;
    const detail = await getContractById(session, contractId);
    setSelectedContractDetail(detail);
    setContracts((prev) => prev.map((contract) => (contract.id === contractId ? mapApiContractToUi(detail) : contract)));
    setSelectedContractId(contractId);
  }

  async function onChangeMeter(payload: ChangeContractMeterPayload): Promise<void> {
    if (!session || !selectedContract) return;
    setIsSavingMeterChange(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await changeContractMeter(session, selectedContract.id, payload);
      await refreshSelectedContract(selectedContract.id);
      setShowCambioMedidorModal(false);
      setActiveTab("medidor");
      setSuccessMessage("Cambio de medidor registrado correctamente.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo registrar cambio de medidor");
    } finally {
      setIsSavingMeterChange(false);
    }
  }

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/clientes")} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer">
        <i className="ri-arrow-left-line" />
        Volver a Clientes
      </button>

      {error && <div className="p-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded">{error}</div>}
      {successMessage && <div className="p-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded">{successMessage}</div>}

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        {loadingCustomer ? (
          <div className="animate-pulse h-16 bg-gray-100 rounded" />
        ) : (
          <div className="flex items-start gap-5 flex-wrap">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-blue-100 text-blue-700 text-xl font-bold">
              {customer.nombre.split(" ").map((n) => n[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl font-bold text-gray-800">{customer.nombre}</h1>
                <Badge variant={estadoVariant[customer.estado]}>{estadoLabel[customer.estado]}</Badge>
              </div>
              <div className="flex items-center gap-4 mt-2 flex-wrap text-sm text-gray-500">
                <span><i className="ri-id-card-line text-gray-400" /> {customer.rut}</span>
                <span><i className="ri-phone-line text-gray-400" /> {customer.telefono || "-"}</span>
                <span><i className="ri-mail-line text-gray-400" /> {customer.email || "-"}</span>
                <span><i className="ri-map-pin-line text-gray-400" /> {customer.direccion || "-"}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canUpdateRut ? (
                <button onClick={() => setShowActualizarRutModal(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100">
                  <i className="ri-id-card-line" /> Actualizar RUT
                </button>
              ) : null}
              <button onClick={() => setShowEditarModal(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                <i className="ri-edit-line" /> Editar cliente
              </button>
              <button onClick={() => setShowConvenioModal(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700">
                <i className="ri-file-list-3-line" /> Convenios
              </button>
              <button onClick={() => setShowRepactacionModal(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600">
                <i className="ri-refresh-line" /> Repactaciones
              </button>
              <button onClick={() => setShowCompromisoModal(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                <i className="ri-shake-hands-line" /> Compromisos
              </button>
              <button onClick={() => setShowNuevoContratoModal(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700">
                <i className="ri-file-add-line" /> Contrato
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-700">Contratos ({contracts.length})</h3>
          {selectedContract ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowOwnershipHistoryModal(true)}
                className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                <i className="ri-history-line" />
                Historial vigencia
                <span className="rounded-full bg-white px-1.5 py-0.5 text-[11px] text-slate-500">
                  {selectedContractDetail?.ownershipChanges.length ?? 0}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setShowRutHistoryModal(true)}
                className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                <i className="ri-id-card-line" />
                Historial RUT
                <span className="rounded-full bg-white px-1.5 py-0.5 text-[11px] text-slate-500">
                  {customer.rutChanges.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setShowCambioMedidorModal(true)}
                className="flex items-center gap-2 rounded-lg bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100"
              >
                <i className="ri-repeat-2-line" />
                Cambio de medidor
              </button>
              <button
                type="button"
                onClick={() => setShowTransferContractModal(true)}
                className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
              >
                <i className="ri-user-shared-line" />
                Traspasar contrato
              </button>
              <button
                type="button"
                onClick={() => setShowOwnershipActionModal(true)}
                className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100"
              >
                <i className="ri-user-unfollow-line" />
                Quitar vigencia
              </button>
            </div>
          ) : null}
        </div>
        {loadingContracts ? (
          <div className="animate-pulse h-10 bg-gray-100 rounded" />
        ) : contracts.length === 0 ? (
          <div className="text-sm text-gray-500">Sin contratos para este cliente.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {contracts.map((contract) => {
              const active = selectedContract?.id === contract.id;
              return (
                <div key={contract.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${active ? "border-teal-500 bg-teal-50" : "border-gray-200 bg-white"}`}>
                  <button onClick={() => setSelectedContractId(contract.id)} className={`text-sm font-medium ${active ? "text-teal-700" : "text-gray-700"}`}>
                    <span>{contract.contractNumber || contract.id} | {contract.medidor}</span>
                    {contract.billingRecipientName && contract.billingRecipientName !== customer.nombre ? (
                      <span className="block text-xs font-normal text-gray-500">
                        Receptor: {contract.billingRecipientName}
                      </span>
                    ) : null}
                  </button>
                  <Badge variant={contractEstadoVariant[contract.estado]} size="sm">{contract.estado}</Badge>
                  <button title="Editar contrato" onClick={() => openEditContrato(contract)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white text-gray-500">
                    <i className="ri-edit-line" />
                  </button>
                  {active ? (
                    <>
                      <button
                        title="Cambio de medidor"
                        onClick={() => setShowCambioMedidorModal(true)}
                        className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white text-teal-600"
                      >
                        <i className="ri-repeat-2-line" />
                      </button>
                      <button
                        title="Quitar vigencia"
                        onClick={() => setShowOwnershipActionModal(true)}
                        className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white text-amber-600"
                      >
                        <i className="ri-user-unfollow-line" />
                      </button>
                      <button
                        title="Traspasar contrato"
                        onClick={() => setShowTransferContractModal(true)}
                        className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white text-blue-600"
                      >
                        <i className="ri-user-shared-line" />
                      </button>
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border p-4">
        <div className="text-xs text-gray-500 mb-1">Deuda Total</div>
        <div className={`text-2xl font-bold ${deudaTotal > 0 ? "text-red-600" : "text-emerald-600"}`}>{deudaTotal > 0 ? `$${deudaTotal.toLocaleString("es-CL")}` : "Al dia"}</div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium ${activeTab === tab.id ? "text-teal-600 border-b-2 border-teal-600 bg-teal-50/50" : "text-gray-500 hover:bg-gray-50"}`}>
              <i className={tab.icon} />{tab.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {!selectedContract && <p className="text-sm text-gray-500">Selecciona o crea un contrato para ver el detalle.</p>}
          {loadingTab && selectedContract && <div className="animate-pulse h-28 bg-gray-100 rounded" />}

          {selectedContract && !loadingTab && activeTab === "consumo" && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-800">Historial de consumo</div>
                  <div className="text-xs text-gray-400">Contrato {selectedContract.contractNumber} · Medidor {selectedContract.medidor || "-"}</div>
                </div>
                <div className="text-xs text-gray-400">{consumo.length} período{consumo.length !== 1 ? "s" : ""}</div>
              </div>

              {consumo.length === 0 ? (
                <div className="py-12 text-center">
                  <i className="ri-bar-chart-2-line text-3xl text-gray-200" />
                  <p className="mt-2 text-sm text-gray-400">Sin lecturas registradas para este contrato.</p>
                </div>
              ) : (
                <>
                  {/* Resumen */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                      <div className="text-xs text-gray-500">Promedio mensual</div>
                      <div className="mt-1 text-xl font-bold text-gray-800">{promedioConsumo.toLocaleString("es-CL")} <span className="text-sm font-normal text-gray-400">m³</span></div>
                    </div>
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                      <div className="text-xs text-gray-500">Máximo registrado</div>
                      <div className="mt-1 text-xl font-bold text-gray-800">{maxConsumo.toLocaleString("es-CL")} <span className="text-sm font-normal text-gray-400">m³</span></div>
                    </div>
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                      <div className="text-xs text-gray-500">Total acumulado</div>
                      <div className="mt-1 text-xl font-bold text-gray-800">{totalConsumo.toLocaleString("es-CL")} <span className="text-sm font-normal text-gray-400">m³</span></div>
                    </div>
                  </div>

                  {/* Gráfico de barras */}
                  <div className="rounded-xl border border-gray-100 p-4">
                    <div className="flex items-end gap-1.5 overflow-x-auto">
                      {consumo.map((item) => {
                        const BAR_MAX_PX = 120;
                        const barPx = item.val === 0 ? 4 : Math.max(Math.round((item.val / maxConsumo) * BAR_MAX_PX), 6);
                        const isMax = item.val === maxConsumo && maxConsumo > 0;
                        return (
                          <div key={item.fecha} className="flex flex-col items-center gap-1 min-w-[2.5rem] flex-1">
                            <span className="text-[10px] text-gray-500 font-medium leading-none">{item.val > 0 ? item.val : ""}</span>
                            <div
                              className={`w-full rounded-t ${isMax ? "bg-teal-500" : "bg-teal-200"}`}
                              style={{ height: `${barPx}px` }}
                              title={`${item.mes}: ${item.val} m³`}
                            />
                            <span className="text-[10px] text-gray-400 whitespace-nowrap">{item.mes}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Tabla detalle */}
                  <div className="rounded-xl border border-gray-100 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-xs text-gray-500 font-medium">
                          <th className="px-4 py-3 text-left">Período</th>
                          <th className="px-4 py-3 text-right">Lect. anterior</th>
                          <th className="px-4 py-3 text-right">Lect. actual</th>
                          <th className="px-4 py-3 text-right">Consumo m³</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {[...consumo].reverse().map((item) => (
                          <tr key={item.fecha} className="hover:bg-gray-50/50">
                            <td className="px-4 py-3 text-gray-700">
                              {new Date(item.fecha).toLocaleDateString("es-CL", { month: "long", year: "numeric" })}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500">{item.anterior != null ? item.anterior.toLocaleString("es-CL") : "-"}</td>
                            <td className="px-4 py-3 text-right text-gray-500">{item.actual != null ? item.actual.toLocaleString("es-CL") : "-"}</td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-800">{item.val.toLocaleString("es-CL")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {selectedContract && !loadingTab && activeTab === "medidor" && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-800">Medidor actual del contrato</div>
                  <div className="text-xs text-gray-500">El cambio queda registrado sobre el contrato y mantiene historial.</div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCambioMedidorModal(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700"
                >
                  <i className="ri-repeat-2-line" />
                  Cambio de medidor
                </button>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-gray-100 bg-slate-50 p-4">
                  <div className="text-xs text-gray-500">Número medidor</div>
                  <div className="mt-2 text-lg font-semibold text-gray-900">{selectedContract.medidor || "-"}</div>
                </div>
                <div className="rounded-xl border border-gray-100 bg-slate-50 p-4">
                  <div className="text-xs text-gray-500">Marca y tipo</div>
                  <div className="mt-2 text-sm font-semibold text-gray-900">
                    {selectedContractDetail?.meterBrand || selectedContract.meterBrand || "Sin marca"} · {selectedContract.tipoMedidor || "-"}
                  </div>
                </div>
                <div className="rounded-xl border border-gray-100 bg-slate-50 p-4">
                  <div className="text-xs text-gray-500">Sello y año</div>
                  <div className="mt-2 text-sm font-semibold text-gray-900">
                    {selectedContract.selloMedidor || "-"} · {selectedContractDetail?.meterManufacturingYear ?? selectedContract.meterManufacturingYear ?? "-"}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-gray-100">
                <div className="border-b border-gray-100 px-4 py-3">
                  <div className="text-sm font-semibold text-gray-800">Historial de cambios de medidor</div>
                </div>
                <div className="space-y-3 p-4">
                  {selectedContractDetail?.meterChanges.length ? (
                    selectedContractDetail.meterChanges.map((change) => (
                      <div key={change.id} className="rounded-xl border border-gray-100 bg-white p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {change.newMeter?.serialNumber || "Medidor sin serie"} · {change.newMeter?.meterType || "Sin tipo"}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              Instalado el {change.installedAt ? new Date(change.installedAt).toLocaleDateString("es-CL") : "-"}
                              {change.readingPeriodCode ? ` · ${change.readingPeriodCode}` : ""}
                            </div>
                          </div>
                          <Badge variant={change.chargeToCustomer ? "warning" : "neutral"} size="sm">
                            {change.chargeToCustomer ? "Con cobro" : "Sin cobro"}
                          </Badge>
                        </div>

                        <div className="mt-3 grid gap-3 text-sm md:grid-cols-3">
                          <div className="rounded-lg bg-slate-50 px-3 py-2">
                            <div className="text-xs text-slate-500">Lectura retiro</div>
                            <div className="font-medium text-slate-800">{change.oldMeterLastReading?.toLocaleString("es-CL") ?? "-"}</div>
                          </div>
                          <div className="rounded-lg bg-slate-50 px-3 py-2">
                            <div className="text-xs text-slate-500">Consumo previo</div>
                            <div className="font-medium text-slate-800">{change.oldMeterConsumptionM3?.toLocaleString("es-CL") ?? "-"} m³</div>
                          </div>
                          <div className="rounded-lg bg-slate-50 px-3 py-2">
                            <div className="text-xs text-slate-500">Lectura inicial nueva</div>
                            <div className="font-medium text-slate-800">{change.initialReading.toLocaleString("es-CL")} m³</div>
                          </div>
                        </div>

                        {change.reason ? <div className="mt-3 text-sm text-gray-600">{change.reason}</div> : null}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">Todavía no hay cambios de medidor registrados para este contrato.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {selectedContract && !loadingTab && activeTab === "pagos" && (
            <div className="space-y-2">
              {payments.length === 0 ? (
                <p className="text-sm text-gray-500">Sin pagos para este contrato.</p>
              ) : payments.map((p) => {
                const handlePrint = () => {
                  const rows = p.allocations.map((a) => ({
                    label: formatDocumentLabel(a.billingDocument.documentType, a.billingDocument.folio, a.billingDocumentId),
                    dueDate: a.billingDocument.dueDate,
                    amount: a.allocatedAmount,
                  }));
                  const total = rows.reduce((acc, r) => acc + r.amount, 0);
                  const html = buildReceiptHtml({
                    receiptNumber: p.receiptNumber,
                    paidAt: p.paidAt,
                    reference: p.reference || null,
                    customerName: customer.nombre,
                    customerRut: customer.rut,
                    contractNumber: selectedContract.contractNumber,
                    rows,
                    total,
                  });
                  openPrintWindow(html);
                };
                const handleDownload = () => {
                  const rows = p.allocations.map((a) => ({
                    label: formatDocumentLabel(a.billingDocument.documentType, a.billingDocument.folio, a.billingDocumentId),
                    dueDate: a.billingDocument.dueDate,
                    amount: a.allocatedAmount,
                  }));
                  const total = rows.reduce((acc, r) => acc + r.amount, 0);
                  const html = buildReceiptHtml({
                    receiptNumber: p.receiptNumber,
                    paidAt: p.paidAt,
                    reference: p.reference || null,
                    customerName: customer.nombre,
                    customerRut: customer.rut,
                    contractNumber: selectedContract.contractNumber,
                    rows,
                    total,
                  });
                  downloadReceiptHtml(html, p.receiptNumber);
                };
                return (
                  <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-800">{p.paidAt ? new Date(p.paidAt).toLocaleDateString("es-CL") : "Sin fecha"}</div>
                      <div className="text-xs text-gray-500">{p.methodName || "Sin método"}{p.receiptNumber ? ` · N° ${p.receiptNumber}` : ""}</div>
                      {p.allocations.length > 0 && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {p.allocations.map((a) => formatDocumentLabel(a.billingDocument.documentType, a.billingDocument.folio, a.billingDocumentId)).join(", ")}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-800">${Number(p.amount).toLocaleString("es-CL")}</div>
                        <div className="text-xs text-gray-500 capitalize">{p.status}</div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={handlePrint}
                          title="Reimprimir comprobante"
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100"
                        >
                          <i className="ri-printer-line" />
                        </button>
                        <button
                          onClick={handleDownload}
                          title="Descargar comprobante"
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100"
                        >
                          <i className="ri-download-line" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {selectedContract && !loadingTab && activeTab === "documentos" && (
            <div className="space-y-2">
              {documentosContrato.length === 0 ? <p className="text-sm text-gray-500">Sin documentos generados para este contrato.</p> : documentosContrato.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100">
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      {doc.documentType.toUpperCase()} {doc.folio ? `#${doc.folio}` : "(sin folio)"}
                    </div>
                    <div className="text-xs text-gray-500">
                      Emisión: {doc.issueDate ? new Date(doc.issueDate).toLocaleDateString("es-CL") : "-"} | Vence: {doc.dueDate ? new Date(doc.dueDate).toLocaleDateString("es-CL") : "-"}
                    </div>
                    {doc.externalError ? <div className="text-xs text-red-600 mt-1">{doc.externalError}</div> : null}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-800">${Number(doc.totalAmount).toLocaleString("es-CL")}</div>
                    <Badge variant={doc.status === "issued" ? "success" : doc.status === "external_error" ? "error" : "warning"} size="sm">
                      {doc.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedContract && !loadingTab && activeTab === "convenios" && (
            <div className="space-y-2">
              {installments.filter((i) => i.type === "convenio").length === 0 ? (
                <div className="py-8 text-center">
                  <i className="ri-file-list-3-line text-2xl text-gray-300" />
                  <p className="mt-1 text-sm text-gray-500">Sin convenios para este contrato.</p>
                  <button onClick={() => setShowConvenioModal(true)} className="mt-3 text-sm text-teal-600 hover:underline">
                    + Crear convenio
                  </button>
                </div>
              ) : (
                installments.filter((i) => i.type === "convenio").map((c) => (
                  <InstallmentRow key={c.id} item={c} onOpen={() => setShowConvenioModal(true)} />
                ))
              )}
            </div>
          )}

          {selectedContract && !loadingTab && activeTab === "repactaciones" && (
            <div className="space-y-2">
              {installments.filter((i) => i.type === "repactacion").length === 0 ? (
                <div className="py-8 text-center">
                  <i className="ri-refresh-line text-2xl text-gray-300" />
                  <p className="mt-1 text-sm text-gray-500">Sin repactaciones para este contrato.</p>
                  <button onClick={() => setShowRepactacionModal(true)} className="mt-3 text-sm text-amber-600 hover:underline">
                    + Nueva repactación
                  </button>
                </div>
              ) : (
                installments.filter((i) => i.type === "repactacion").map((c) => (
                  <InstallmentRow key={c.id} item={c} onOpen={() => setShowRepactacionModal(true)} />
                ))
              )}
            </div>
          )}

          {selectedContract && !loadingTab && activeTab === "compromisos" && (
            <div className="space-y-2">
              {installments.filter((i) => i.type === "compromiso").length === 0 ? (
                <div className="py-8 text-center">
                  <i className="ri-shake-hands-line text-2xl text-gray-300" />
                  <p className="mt-1 text-sm text-gray-500">Sin compromisos registrados.</p>
                  <button onClick={() => setShowCompromisoModal(true)} className="mt-3 text-sm text-blue-600 hover:underline">
                    + Nuevo compromiso
                  </button>
                </div>
              ) : (
                installments.filter((i) => i.type === "compromiso").map((c) => (
                  <InstallmentRow key={c.id} item={c} onOpen={() => setShowCompromisoModal(true)} />
                ))
              )}
            </div>
          )}

          {selectedContract && !loadingTab && activeTab === "atenciones" && (
            <div className="space-y-2">
              {operations.length === 0 ? <p className="text-sm text-gray-500">Sin atenciones para este contrato.</p> : operations.map((o) => (
                <div key={o.id} className="p-3 rounded-lg border border-gray-100">
                  <div className="text-sm font-semibold text-gray-800">{o.status}</div>
                  <div className="text-xs text-gray-600">{o.requestSummary}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showNuevoContratoModal && <NuevoContratoModal cliente={{ id: customer.id, nombre: customer.nombre, rut: customer.rut, direccion: customer.direccion, sector: "", customerType: customer.customerType }} tariffOptions={tariffOptions} sectorOptions={sectorOptions} onClose={() => setShowNuevoContratoModal(false)} onCreated={onCreateContract} />}
      {showEditarModal && <EditarClienteModal cliente={{ id: customer.id, nombre: customer.nombre, rut: customer.rut, categoria: customer.customerCategory ?? "usuario", genero: customer.gender ?? "", telefono: customer.telefono, email: customer.email, direccion: customer.direccion, estado: customer.estado }} onClose={() => setShowEditarModal(false)} onSave={onEditCustomer} />}
      {showActualizarRutModal ? (
        <ActualizarRutModal
          currentRut={customer.rut}
          customerName={customer.nombre}
          contracts={contracts.map((contract) => ({
            id: contract.id,
            contractNumber: contract.contractNumber || contract.id,
            sector: contract.sector,
            address: contract.direccion,
          }))}
          saving={isSavingRut}
          onClose={() => setShowActualizarRutModal(false)}
          onSave={onUpdateRut}
        />
      ) : null}

      {showOwnershipHistoryModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowOwnershipHistoryModal(false)} />
          <div className="relative w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Historial de vigencia y titularidad</h3>
                <p className="text-sm text-gray-500">
                  {selectedContract ? `${selectedContract.contractNumber || selectedContract.id} - ${customer.nombre}` : "Selecciona un contrato"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowOwnershipHistoryModal(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="max-h-[70vh] overflow-auto p-6">
              {!selectedContract ? (
                <div className="text-sm text-gray-500">Selecciona un contrato para ver su historial.</div>
              ) : !selectedContractDetail ? (
                <div className="text-sm text-gray-500">Cargando historial del contrato...</div>
              ) : selectedContractDetail.ownershipChanges.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 p-6 text-sm text-gray-500">
                  Sin cambios de vigencia o titularidad registrados.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 text-left text-gray-500">
                        <th className="py-2 pr-4 font-medium">Evento</th>
                        <th className="py-2 pr-4 font-medium">Desde</th>
                        <th className="py-2 pr-4 font-medium">Hacia</th>
                        <th className="py-2 pr-4 font-medium">Deuda</th>
                        <th className="py-2 pr-4 font-medium">Fecha</th>
                        <th className="py-2 font-medium">Usuario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedContractDetail.ownershipChanges.map((change) => (
                        <tr key={change.id} className="border-b border-gray-50 last:border-b-0">
                          <td className="py-3 pr-4 text-gray-800">
                            {change.eventType === "transfer_holder" ? "Traspaso" : "Quitar vigencia"}
                          </td>
                          <td className="py-3 pr-4 text-gray-600">
                            {change.fromCustomerName || "-"}{change.fromCustomerRut ? ` - ${change.fromCustomerRut}` : ""}
                          </td>
                          <td className="py-3 pr-4 text-gray-600">
                            {change.toCustomerName || "-"}{change.toCustomerRut ? ` - ${change.toCustomerRut}` : ""}
                          </td>
                          <td className="py-3 pr-4 text-gray-600">
                            {change.debtTransferredAmount > 0 ? `$${change.debtTransferredAmount.toLocaleString("es-CL")}` : "-"}
                          </td>
                          <td className="py-3 pr-4 text-gray-600">{formatRutChangeDate(change.createdAt)}</td>
                          <td className="py-3 text-gray-600">{change.changedByName || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showRutHistoryModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowRutHistoryModal(false)} />
          <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Historial de RUT</h3>
                <p className="text-sm text-gray-500">{customer.nombre} - {customer.rutChanges.length} cambio(s)</p>
              </div>
              <button
                type="button"
                onClick={() => setShowRutHistoryModal(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="max-h-[70vh] overflow-auto p-6">
              {customer.rutChanges.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 p-6 text-sm text-gray-500">
                  Sin cambios de RUT registrados.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 text-left text-gray-500">
                        <th className="py-2 pr-4 font-medium">RUT anterior</th>
                        <th className="py-2 pr-4 font-medium">RUT nuevo</th>
                        <th className="py-2 pr-4 font-medium">Fecha</th>
                        <th className="py-2 font-medium">Usuario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customer.rutChanges.map((change) => (
                        <tr key={change.id} className="border-b border-gray-50 last:border-b-0">
                          <td className="py-3 pr-4 text-gray-700">{change.previousRut}</td>
                          <td className="py-3 pr-4 font-medium text-gray-800">{change.newRut}</td>
                          <td className="py-3 pr-4 text-gray-600">{formatRutChangeDate(change.changedAt)}</td>
                          <td className="py-3 text-gray-600">{change.changedByName || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showOwnershipActionModal && selectedContract ? (
        <QuitarVigenciaContratoModal
          session={session!}
          contract={{
            id: selectedContract.id,
            contractNumber: selectedContract.contractNumber || selectedContract.id,
            direccion: selectedContract.direccion,
            sector: selectedContract.sector,
          }}
          currentCustomerId={customer.id}
          currentCustomerName={customer.nombre}
          initialAction="deactivate"
          lockAction
          onClose={() => setShowOwnershipActionModal(false)}
          onCompleted={onOwnershipActionCompleted}
        />
      ) : null}

      {showTransferContractModal && selectedContract ? (
        <QuitarVigenciaContratoModal
          session={session!}
          contract={{
            id: selectedContract.id,
            contractNumber: selectedContract.contractNumber || selectedContract.id,
            direccion: selectedContract.direccion,
            sector: selectedContract.sector,
          }}
          currentCustomerId={customer.id}
          currentCustomerName={customer.nombre}
          initialAction="transfer"
          lockAction
          onClose={() => setShowTransferContractModal(false)}
          onCompleted={onOwnershipActionCompleted}
        />
      ) : null}

      {showCambioMedidorModal && selectedContract ? (
        <CambioMedidorModal
          contract={{
            contractNumber: selectedContract.contractNumber,
            meterSerialNumber: selectedContract.medidor,
            meterType: selectedContract.tipoMedidor,
            meterBrand: selectedContractDetail?.meterBrand || selectedContract.meterBrand,
            meterSealNumber: selectedContract.selloMedidor,
            customerName: customer.nombre,
            address: selectedContract.direccion,
            sector: selectedContract.sector,
          }}
          lastReading={
            latestReading
              ? {
                  currentReading: latestReading.currentReading,
                  readAt: latestReading.readAt,
                }
              : null
          }
          saving={isSavingMeterChange}
          onClose={() => setShowCambioMedidorModal(false)}
          onSubmit={onChangeMeter}
        />
      ) : null}

      {showEditContratoModal && selectedContract && <NuevoContratoModal cliente={{ id: customer.id, nombre: customer.nombre, rut: customer.rut, direccion: customer.direccion, sector: "", customerType: customer.customerType }} tariffOptions={tariffOptions} sectorOptions={sectorOptions} mode="edit" initialValues={{ address: selectedContract.direccion, startDate: selectedContract.fechaAlta, sectorId: sectorOptions.find((s) => s.name === selectedContract.sector)?.id ?? sectorOptions[0]?.id ?? "", tariffId: tariffOptions.find((t) => t.name === selectedContract.tarifa)?.id ?? tariffOptions[0]?.id ?? "", meterType: selectedContract.tipoMedidor, meterNumber: selectedContract.medidor, sealNumber: selectedContract.selloMedidor, status: selectedContract.estado, billingDocumentType: selectedContract.billingDocumentType, billingRecipientName: selectedContract.billingRecipientName ?? "" }} onClose={() => setShowEditContratoModal(false)} onCreated={onEditContract} />}

      {showConvenioModal && selectedContract ? (
        <ConvenioModal
          contractId={selectedContract.id}
          contractNumber={selectedContract.contractNumber}
          deudaTotal={deudaTotal}
          onClose={() => setShowConvenioModal(false)}
          onChanged={() => { void listInstallmentsByContract(session!, selectedContract.id).then(setInstallments); }}
        />
      ) : null}

      {showRepactacionModal && selectedContract ? (
        <RepactacionModal
          contractId={selectedContract.id}
          contractNumber={selectedContract.contractNumber}
          deudaTotal={deudaTotal}
          deudaBoletas={deudaBoletas}
          onClose={() => setShowRepactacionModal(false)}
          onChanged={() => { void listInstallmentsByContract(session!, selectedContract.id).then(setInstallments); }}
        />
      ) : null}

      {showCompromisoModal && selectedContract ? (
        <CompromisoModal
          contractId={selectedContract.id}
          contractNumber={selectedContract.contractNumber}
          deudaTotal={deudaTotal}
          onClose={() => setShowCompromisoModal(false)}
          onChanged={() => { void listInstallmentsByContract(session!, selectedContract.id).then(setInstallments); }}
        />
      ) : null}
    </div>
  );
}

function InstallmentRow({ item, onOpen }: { item: InstallmentItem; onOpen: () => void }) {
  const statusLabel: Record<string, string> = { active: "Activo", completed: "Completado", cancelled: "Anulado", paused: "Pausado" };
  const statusClass: Record<string, string> = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    completed: "bg-blue-50 text-blue-700 border-blue-200",
    cancelled: "bg-gray-100 text-gray-500 border-gray-200",
    paused: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <div className={`p-3 rounded-xl border ${item.status === "cancelled" ? "opacity-60 border-gray-100" : "border-gray-100"}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">${Number(item.totalAmount).toLocaleString("es-CL")}</span>
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${statusClass[item.status] ?? statusClass.active}`}>
              {statusLabel[item.status] ?? item.status}
            </span>
          </div>
          {item.installments > 1 ? (
            <p className="mt-0.5 text-xs text-gray-500">
              {item.installments} cuotas de ${Number(item.installmentAmount).toLocaleString("es-CL")} · desde {item.startPeriod}
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-gray-500">para {item.startPeriod}</p>
          )}
          {item.notes ? <p className="mt-0.5 text-xs text-gray-400 italic">{item.notes}</p> : null}
        </div>
        <button onClick={onOpen} className="text-xs text-gray-400 hover:text-teal-600">
          <i className="ri-eye-line" />
        </button>
      </div>
    </div>
  );
}

function mapApiContractToUi(contract: ContractListItem): UiContract {
  return {
    id: contract.id,
    customerId: contract.customerId,
    meterId: contract.meterId,
    contractNumber: contract.contractNumber,
    direccion: contract.startAddress,
    sector: contract.sector || "-",
    estado: contract.status === "active" ? "vigente" : "suspendido",
    tarifa: contract.tariffName || "-",
    medidor: contract.meterSerialNumber || "-",
    tipoMedidor: contract.meterType || "-",
    selloMedidor: contract.meterSealNumber || "",
    billingRecipientName: contract.billingRecipientName || contract.billingProfile?.recipientName || undefined,
    fechaAlta: contract.contractDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
    billingDocumentType: normalizeBillingDocumentType(contract.billingDocumentType),
    meterBrand: contract.meterBrand || undefined,
    meterManufacturingYear: contract.meterManufacturingYear ?? null,
  };
}

function normalizeBillingDocumentType(value?: string): UiContract["billingDocumentType"] {
  return value === "33" || value === "34" || value === "39" || value === "41" ? value : "39";
}

async function saveContractBillingRecipient(
  session: AdminSession,
  contractId: string,
  customerRut: string,
  recipientName: string,
): Promise<void> {
  const cleanedRecipientName = recipientName.trim();
  if (!cleanedRecipientName) {
    await deleteContractBillingProfile(session, contractId);
    return;
  }
  const parsedRut = splitRut(customerRut);
  await upsertContractBillingProfile(session, contractId, {
    recipientRut: parsedRut.rut,
    recipientDv: parsedRut.dv,
    recipientName: cleanedRecipientName,
  });
}

function splitRut(input: string): { rut: string; dv: string } {
  const cleaned = input.replace(/\./g, "").replace(/\s+/g, "").trim();
  const parts = cleaned.split("-");
  return {
    rut: parts[0] ?? "",
    dv: (parts[1] ?? "").toUpperCase(),
  };
}

function formatRutChangeDate(value: string): string {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("es-CL");
}
